import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for auto-refreshing data
 * @param {Function} refreshCallback - Function to call on refresh
 * @param {number} interval - Refresh interval in milliseconds (default: 30000 = 30 seconds)
 * @param {boolean} initialEnabled - Whether auto-refresh is enabled initially (default: true)
 */
export function useAutoRefresh(refreshCallback, interval = 30000, initialEnabled = true) {
  const [isEnabled, setIsEnabled] = useState(initialEnabled);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [nextRefresh, setNextRefresh] = useState(Date.now() + interval);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const intervalRef = useRef(null);
  const nextRefreshTimerRef = useRef(null);

  const refresh = useCallback(async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      await refreshCallback();
      setLastRefresh(Date.now());
      setNextRefresh(Date.now() + interval);
    } catch (error) {
      console.error('Auto-refresh error:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshCallback, interval, isRefreshing]);

  const toggle = useCallback(() => {
    setIsEnabled(prev => !prev);
  }, []);

  const setEnabled = useCallback((enabled) => {
    setIsEnabled(enabled);
  }, []);

  // Setup auto-refresh interval
  useEffect(() => {
    if (isEnabled) {
      // Start interval
      intervalRef.current = setInterval(refresh, interval);
      
      // Update next refresh timer every second
      nextRefreshTimerRef.current = setInterval(() => {
        setNextRefresh(prev => prev);
      }, 1000);
      
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        if (nextRefreshTimerRef.current) {
          clearInterval(nextRefreshTimerRef.current);
        }
      };
    } else {
      // Clear intervals when disabled
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (nextRefreshTimerRef.current) {
        clearInterval(nextRefreshTimerRef.current);
      }
    }
  }, [isEnabled, interval, refresh]);

  // Calculate seconds until next refresh
  const secondsUntilRefresh = Math.max(0, Math.ceil((nextRefresh - Date.now()) / 1000));

  return {
    isEnabled,
    toggle,
    setEnabled,
    refresh,
    isRefreshing,
    lastRefresh,
    secondsUntilRefresh
  };
}

